/**
 * Identifies which Google-for-Jobs auto-click-apply variant should fire on the
 * ViewJob page. Both string values correspond to `ReceivedFlagType` enum entries
 * on the mweb server, so they can be emitted directly as `mobRcvFlag` `flag`
 * field values without further translation.
 */
export type AutoClickApplyFlag = 'autoClickApplyIA' | 'autoClickApplyNonIA';

/**
 * Resolves the two auto-click variants independently from the server-set
 * booleans, mirroring jobseeker-frontend: the IA flag drives the Indeed Apply
 * button and the non-IA flag drives the third-party apply button. They are kept
 * separate (no precedence/collapsing) so each button fires and logs only on its
 * own signal — otherwise, e.g., a third-party job would auto-click off the IA
 * bucket boolean and mis-log as `autoClickApplyIA`.
 */
export function getAutoClickApplyFlags(autoClickFlags: {
    autoClickApply?: boolean;
    autoClickApplyNonIa?: boolean;
}): {
    autoClickApplyIaFlag: AutoClickApplyFlag | null;
    autoClickApplyNonIaFlag: AutoClickApplyFlag | null;
} {
    return {
        autoClickApplyIaFlag: autoClickFlags.autoClickApply ? 'autoClickApplyIA' : null,
        autoClickApplyNonIaFlag: autoClickFlags.autoClickApplyNonIa ? 'autoClickApplyNonIA' : null,
    };
}

/**
 * Whether a logged-out third-party apply should be intercepted by the Passport
 * sign-in gate. The backend only populates `passportUrl`
 * (`thirdPartyApplyCreateAccountModel.contextualSignInPassportURL`) when the
 * intercept should run, so its presence is the canonical gate signal — shared
 * by both the manual click (`usePassportLoginGate`) and the GFJ auto-click
 * (`getAutoClickUrl`) so the two entry points can never disagree.
 */
export function shouldGateThirdPartyApplyWithPassport(params: {
    isLoggedIn: boolean;
    passportUrl: string;
}): boolean {
    return !params.isLoggedIn && params.passportUrl !== '';
}

/**
 * Resolves the URL the Google-for-Jobs auto-click should navigate to, enforcing
 * the Passport login gate.
 *
 * A logged-out user is auto-redirected to Passport login
 * (`contextualSignInPassportURL`) so the gate runs before they reach the
 * third-party apply page — the auto-click fires before they can interact, so
 * without this they'd skip the intercept entirely.
 *
 * For a logged-out third-party apply job the passport URL is expected to ALWAYS
 * be present, so the empty-URL branch should not happen in practice. It is a
 * defensive fallback: with no URL there is nothing to gate with, so rather than
 * strand a GFJ user who arrived expecting to apply we let the auto-click proceed
 * to apply. (See follow-up: whether to instead trigger the login modal here.)
 *
 * Logged-in users auto-click straight to the apply link. Auto-click is web-only
 * — the native hook is a no-op, so on native this always returns null.
 */
export function getAutoClickUrl(params: {
    isLoggedIn: boolean;
    passportUrl: string;
    applyLink: string | null | undefined;
    isWeb: boolean;
}): string | null {
    const { isLoggedIn, passportUrl, applyLink, isWeb } = params;
    if (!isWeb) {
        return null;
    }
    if (shouldGateThirdPartyApplyWithPassport({ isLoggedIn, passportUrl })) {
        return passportUrl;
    }
    return applyLink ?? null;
}
